import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Text,
  Spinner,
  Grid,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import showToast from "hooks/useCustomToast";
import DocModal from "../../components/new_darwin_eva/DocModal";
import EvaAgentLeftPanel from "../../components/new_darwin_eva/EvaAgentLeftPanel";
import EvaAgentRightPanelWrapper from "../../components/new_darwin_eva/EvaAgentRightPanelWrapper";
import { DarwinAgent, DarwinTool, PromptProto } from "build-ts/darwin_agent";
import { JinjaEnv } from "build-ts/jinja";
import { createNewAgent, getToolsList, getSystemPromptDocumentation } from "services/NewDarwinEvaServices";
import { darwinAgentTypeFromJSON } from "build-ts/common";
import { ReasoningEffortLevel } from "build-ts/common";

const EvaAgentCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [promptEditor, setPromptEditor] = useState("");
  const [agent, setAgentConfig] = useState<DarwinAgent>({
    header: {
      id: "",
      name: "",
      description: "",
      type: "",
      is_active: false,
    },
    prompts: [],
    tools: [],
    context_template: "",
  });

  // Separate prompt configuration state
  const [promptConfig, setPromptConfig] = useState<PromptProto>({
    header: {
      id: "",
      description: "",
      is_active: true,
      version: 1,
    },
    model_config: {
      model_name: "gpt-4o",
      model_provider: "openai",
      temperature: 0,
      reasoning_effort_level: ReasoningEffortLevel.REASONING_NOT_VALID,
    },
    template: promptEditor.trim() || "",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [availableTools, setAvailableTools] = useState<DarwinTool[]>([]);
  const [, setIsSaving] = useState(false);

  // Documentation modal states
  const [availableVariables, setAvailableVariables] = useState<JinjaEnv | null>(null);
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);
  const { isOpen: isDocsOpen, onOpen: onDocsOpen, onClose: onDocsClose } = useDisclosure();

  const fetchToolsList = async () => {
    try {
      const params = new URLSearchParams({
        limit: "100",
        offset: "0",
        sort_by: "name",
        sort_order: "asc",
      });

      const data = await getToolsList(params);
      return data.tools || [];
    } catch (error) {
      console.error("Error fetching tools list:", error);
      showToast("Failed to fetch tools list", "", "error");
      throw error;
    }
  };

  // API call to fetch system prompt documentation
  const fetchSystemPromptDocumentation = async () => {
    try {
      setIsLoadingDoc(true);
      const data = await getSystemPromptDocumentation();

      // Set the JinjaEnv data directly from the API response
      if (data) {
        setAvailableVariables(data);
      } else {
        setAvailableVariables(null);
      }
    } catch (error) {
      console.error("Error fetching system prompt documentation:", error);
      showToast("Failed to fetch documentation", "", "error");
      setAvailableVariables(null);
    } finally {
      setIsLoadingDoc(false);
    }
  };

  const handleViewDocs = async () => {
    onDocsOpen();
    // Fetch documentation when modal opens
    await fetchSystemPromptDocumentation();
  };

  // Load tools list on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const toolsData = await fetchToolsList();
        setAvailableTools(toolsData);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfigChange = (field: string, value: any) => {
    setAgentConfig(prev => {
      // Handle nested field updates (e.g., "header.name")
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        if (parentField === "header") {
          return {
            ...prev,
            header: {
              ...prev.header,
              [childField]: value
            }
          };
        }
      }

      // Handle other direct field updates
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handleToolsChange = (selectedTools: (DarwinTool | any)[]) => {
    setAgentConfig(prev => ({
      ...prev,
      tools: selectedTools as DarwinTool[]
    }));
  };

  const handlePromptConfigChange = (field: string, value: any) => {
    // Update separate prompt config state
    setPromptConfig(prev => {
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        if (parentField === "header") {
          return {
            ...prev,
            header: {
              ...prev.header,
              [childField]: value
            }
          };
        } else if (parentField === "model_config") {
          return {
            ...prev,
            model_config: {
              ...prev.model_config,
              [childField]: value
            }
          };
        }
      }
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handlePromptEditorChange = (value: string) => {
    setPromptEditor(value);
    setPromptConfig(prev => ({
      ...prev,
      template: value
    }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!agent.header?.name?.trim()) {
      showToast("Validation Error", "Agent name is required", "error");
      return;
    }

    if (!agent.header?.type) {
      showToast("Validation Error", "Agent type is required", "error");
      return;
    }

    setIsSaving(true);
    try {
      const agentData: DarwinAgent = {
        header: {
          id: agent.header?.id || "",
          name: agent.header?.name?.trim() || "",
          description: agent.header?.description?.trim() || "",
          type: agent.header?.type || "",
          is_active: agent.header?.is_active ?? true,
        },
        prompts: [{
          header: {
            id: promptConfig.header?.id || "",
            name: "PROMPT",
            version: promptConfig.header?.version || 1,
            description: promptConfig.header?.description || "",
            is_active: promptConfig.header?.is_active ?? true,
          },
          model_config: {
            model_name: promptConfig.model_config?.model_name || "gpt-4o",
            model_provider: promptConfig.model_config?.model_provider || "openai",
            temperature: promptConfig.model_config?.temperature || 0,
            reasoning_effort_level: promptConfig.model_config?.reasoning_effort_level || ReasoningEffortLevel.REASONING_NOT_VALID,
          },
          template: promptEditor.trim() || "",
        }],
        tools: agent.tools,
        context_template: agent.context_template?.trim() || undefined,
      };

      const createdAgent = await createNewAgent(agentData);

      // Only proceed if agent creation was successful
      if (createdAgent) {
        showToast("Success", "Agent created successfully", "success");
        // Navigate to the created agent's detail page
        navigate(`/eva/${createdAgent.header?.id}`);
      }
    } catch (error: any) {
      showToast("Failed to create agent", error?.message || "Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/eva");
  };


  if (isLoading) {
    return (
      <Box className="eva-container eva-small-scrollbar" display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading agent data...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box bg="gray.100" p={2} height="100vh" overflow="hidden">
    <Grid
      height="100%"
      width="100%"
      templateColumns="minmax(0, 25%) minmax(0, 75%)"
      gap={1}
    >
        {/* Left Panel - Agent Configuration (25%) */}
        <EvaAgentLeftPanel
          agent={agent}
          availableTools={availableTools}
          onConfigChange={handleConfigChange}
          onToolsChange={handleToolsChange}
          isCreateMode={true}
          onCancel={handleCancel}
          onSave={handleSave}
          onViewDocs={handleViewDocs}
        />

        {/* Right Panel - Prompt Editor (75%) */}
        <EvaAgentRightPanelWrapper
          agent={agent}
          promptConfig={promptConfig}
          promptEditor={promptEditor}
          onPromptEditorChange={handlePromptEditorChange}
          onPromptConfigChange={handlePromptConfigChange}
          isCreateMode={true}
          onViewDocs={handleViewDocs}
        />
      </Grid>

      {/* Variables Documentation Modal */}
      <DocModal
        isDocsOpen={isDocsOpen}
        onDocsClose={onDocsClose}
        isLoadingDoc={isLoadingDoc}
        availableVariables={availableVariables}
        agentType={agent.header?.type ? darwinAgentTypeFromJSON(agent.header.type) : undefined}
      />
    </Box>
  );
};

export default EvaAgentCreatePage;
